import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

/**
 * The split between the two apps that share this bundle — CA-5, §7.2.
 *
 * ## Why the branch is here, above everything else
 *
 * `/redeem/*` is a customer on a phone in a shop. Everything else is a signed-in
 * client_admin. §7.2 requires the customer tree to **never** load the
 * authenticated shell, `useAuth`, the sidebar, or any `client_admin` code — for
 * two load-bearing reasons: a phone on a bad connection should download a few
 * kB rather than the admin bundle, and admin code that never reaches the public
 * bundle cannot leak anything through it.
 *
 * That means the branch has to happen **before `AuthProvider` mounts**. Putting
 * it inside `AppRoutes`, below the provider, would satisfy the routing and
 * violate the isolation: every customer would still be running the session
 * machinery, and every customer would still be downloading it.
 *
 * ## Both halves are lazy, and that is the point
 *
 * `React.lazy` puts each tree in its own chunk. A customer opening `/redeem/...`
 * fetches the public chunk; the admin chunk is never requested. Making only the
 * public tree lazy would have achieved the opposite of what §7.2 asks for.
 *
 * The fallback is deliberately a bare tinted page rather than a spinner or a
 * skeleton: it is on screen for one chunk fetch, and anything more elaborate
 * would flash.
 */
const PublicApp = lazy(() => import('./features/public/PublicApp'));
const AdminApp = lazy(() => import('./AdminApp'));

function ChunkFallback() {
  return <div className="min-h-dvh bg-background" />;
}

export default function App() {
  return (
    <Suspense fallback={<ChunkFallback />}>
      <Routes>
        {/* The customer journey. No auth, no shell, no admin code. */}
        <Route path="/redeem/*" element={<PublicApp />} />

        {/* Everything else is the authenticated tenant console. */}
        <Route path="/*" element={<AdminApp />} />
      </Routes>
    </Suspense>
  );
}
